import type { AbilityConfigMap } from '@efdigital/ll-addon-auth/types/abilities';

/**
 * Type-safe ability names for ll-admin-v2.
 *
 * Defines all areas of the application that require permission checks.
 * Each ability maps to specific JWT rights in the user's ID token ('https://va/r').
 *
 * Naming Convention:
 * - Base abilities (e.g., 'programs') - View access to a feature area
 * - Action abilities (e.g., 'programs:create') - Specific actions within that area
 */
export type AdminAbilityName =
  | 'programs'
  | 'cohorts'
  | 'participants'
  | 'content'
  | 'calendar'
  | 'clients'
  | 'clients:users' // Special case: different rights pattern for managing client users
  | 'library';

/**
 * Ability configuration for ll-admin-v2.
 *
 * Maps ability names to required JWT rights at each access level.
 * Rights come from Azure AD B2C token claim 'https://va/r'.
 *
 * Access Levels:
 * - viewer: Read-only access (requires read:* rights)
 * - editor: Modify access (requires edit:* rights)
 * - fullcontrol: Administrative access (requires manage:* or purge:* rights)
 *
 * Security Model:
 * - Undefined levels or empty arrays = access denied (secure by default)
 * - All listed rights must be present (AND logic, not OR)
 * - Levels resolve from highest to lowest (fullcontrol → editor → viewer)
 *
 * @see {@link app/services/ability.ts} for usage examples
 */
export const ABILITIES: AbilityConfigMap = {
  programs: {
    rights: {
      viewer: ['read:programs'],
      editor: ['edit:programs'],
      fullcontrol: ['manage:programs'],
    },
  },

  cohorts: {
    rights: {
      viewer: ['read:cohorts'],
      editor: ['edit:cohorts'],
    },
  },

  participants: {
    rights: {
      viewer: ['read:cohorts'],
      editor: ['edit:users'],
    },
  },

  content: {
    rights: {
      viewer: ['read:pages', 'read:resources'],
      editor: ['edit:pages', 'edit:resources'],
    },
  },

  calendar: {
    rights: {
      viewer: ['read:calendaritems'],
      editor: ['edit:calendaritems'],
    },
  },

  clients: {
    rights: {
      viewer: ['read:clients'],
      editor: ['edit:clients'],
      fullcontrol: ['purge:clients'],
    },
  },

  // Special case: managing client users requires different rights
  'clients:users': {
    rights: {
      viewer: ['read:clients'],
      editor: ['edit:users'],
    },
  },

  library: {
    rights: {
      viewer: ['read:mediaimages'],
      editor: ['edit:mediaimages'],
    },
  },
};
